import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiResponse } from '../shared/types';

/**
 * CWS-1778 (story CWS-1534): the cancel & refund flow — what one purchase can
 * do (per item, with server-priced refund options and the reason list), the
 * cancellation itself, and a retry of a refund that failed.
 *
 * Every amount is in cents and comes from svc-order; the panel only formats
 * and forwards. Shapes mirror apps/svc-order/src/services/cancellation/*.
 */

export type CancelRefundAction = 'cancel' | 'refund' | 'mixed' | 'none';
export type CancelRefundItemState = 'live' | 'completed' | 'actioned';
export type RefundOption = 'none' | 'prorated' | 'full' | 'custom';
export type RefundMethod = 'card' | 'cash';
export type CancellationStatus =
  'pending' | 'billing_stopped' | 'refunded' | 'refund_failed' | 'completed' | 'failed';

export interface CancelRefundReason {
  code: string;
  label: string;
}

export interface CancelRefundReasonPrompt {
  title: string;
  helper: string;
}

/** What a prorated figure was computed from: unused days of a cycle, or unused washes. */
export interface ProratedBasis {
  kind: 'days' | 'washes';
  remaining: number;
  total: number;
}

export interface CustomRefundBounds {
  min_cents: number;
  max_cents: number;
}

export interface CancelRefundItem {
  /** `package:<order id>` or `addon:<addon id>`; echoed back on execute. */
  item_id: string;
  kind: 'package' | 'addon';
  label: string;
  amount_cents: number;
  state: CancelRefundItemState;
  action: CancelRefundAction;
  /** Why the item is not actionable, when `action` is `none`. */
  reason: string | null;
  refund_options: RefundOption[];
  refundable_cents: number;
  refund_amounts: {
    none?: number;
    prorated?: number;
    full?: number;
    custom?: CustomRefundBounds;
  };
  refund_basis: { prorated?: ProratedBasis };
}

export interface CancelRefundOptions {
  order_id: string;
  order_number: string;
  subscription_id: string | null;
  customer_id: string;
  location_id: string;
  payment_status: string;
  order_status: string;
  order_action: CancelRefundAction;
  items: CancelRefundItem[];
  refundable_cents: number;
  total_paid_cents: number;
  total_refunded_cents: number;
  /** Where a refund goes; null when nothing was paid. */
  refund_method: RefundMethod | null;
  /** The card a refund returns to ("Visa ··4242"); null when the last charge has no snapshot. */
  refund_destination?: { brand: string | null; last4: string } | null;
  /** The membership behind the order, for "renews Nov 18" and the monthly price. */
  membership?: {
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    last_charge_cents: number;
  } | null;
  /** The prepaid book behind the order, for "2 of 5 washes left", one entry per wash line. */
  prepaid?: {
    remaining_washes: number;
    total_washes: number;
    washes?: { name: string | null; total: number; remaining: number }[];
  } | null;
  reasons: CancelRefundReason[];
  reason_prompt: CancelRefundReasonPrompt;
  /** Both lists, so the panel can follow the selection when it changes the action. */
  reasons_by_action?: { cancel: CancelRefundReason[]; refund: CancelRefundReason[] };
  reason_prompts?: { cancel: CancelRefundReasonPrompt; refund: CancelRefundReasonPrompt };
  prorated_refund_enabled: boolean;
  /**
   * CWS-1579: the plate's family, when it has one. A primary with others (`active_count > 1`)
   * is cancelled through the family flow (Cancel all / Cancel primary only).
   */
  family?: CancelRefundFamily | null;
  /** Ties the execute request to the options the operator saw. */
  version: string;
}

export interface CancelRefundFamily {
  group_id: string;
  member_id: string;
  role: 'primary' | 'member';
  active_count: number;
}

export interface CancelRefundItemChoice {
  item_id: string;
  refund_option: RefundOption;
  custom_amount_cents?: number;
}

/** When a cancelled membership ends: at the period end (the default) or today. */
export type CancelTiming = 'period_end' | 'immediate';

/**
 * CWS-1947: the operator's claim on a cancel they have started.
 *
 * An order auto-completes 60 seconds after payment. Without a hold that flip
 * invalidates the `version` token and the Confirm comes back `changed_refresh`,
 * which is what left an attendant unable to cancel OR refund in production.
 * `expires_at` is the server's deadline -- the countdown runs against it rather
 * than against a locally ticked number, so a slow tablet cannot drift past it.
 */
export interface CancelRefundHold {
  hold_id: string;
  expires_at: string;
  window_seconds: number;
  version: string;
  order_id: string | null;
  subscription_id: string | null;
  action: CancelRefundAction;
}

export interface CancelRefundRequest {
  request_id: string;
  version: string;
  /** CWS-1947: present when the panel holds this order; absent behaves as before. */
  hold_id?: string;
  location_id?: string;
  order_id?: string;
  subscription_id?: string;
  reason_code: string;
  reason_note?: string;
  cancel_timing?: CancelTiming;
  items: CancelRefundItemChoice[];
}

export interface CancelRefundResultLine {
  item_id: string;
  kind: 'package' | 'addon';
  label: string;
  action: CancelRefundAction;
  refund_option: RefundOption;
  requested_cents: number;
  refunded_cents: number;
}

export interface CancelRefundResult {
  replayed: boolean;
  cancellation: { id: string; status: CancellationStatus; action: CancelRefundAction } & Record<
    string,
    unknown
  >;
  status: CancellationStatus;
  order_status: string | null;
  refund: {
    method: RefundMethod | null;
    requested_cents: number;
    refunded_cents: number;
    capped_at_cents: number | null;
    stripe_refund_id: string | null;
    /** Why the refund did not go out, when it did not. */
    failed: string | null;
  };
  membership: Record<string, unknown> | null;
  items: CancelRefundResultLine[];
  /** CWS-1579: what a cancelled member's family became; `pending` when it is still to update. */
  family?: FamilyCancelOutcome;
}

// ----- CWS-1579: family plan cancellation -----

export type FamilyCancelMode = 'all' | 'primary_only';

/** One plate of the family with its own cancel & refund options. */
export interface FamilyCancelPlate {
  member_id: string;
  subscription_id: string;
  role: 'primary' | 'member';
  license_plate: string | null;
  joined_at: string | null;
  /** Already scheduled to end (or ended): it leaves with the rest and is not cancelled again. */
  already_ending: boolean;
  options: CancelRefundOptions;
}

export interface FamilyCancelOptions {
  group_id: string;
  viewer_member_id: string;
  viewer_role: 'primary' | 'member';
  modes: (FamilyCancelMode | 'member')[];
  /** Primary first, then members in the order they joined. */
  members: FamilyCancelPlate[];
  family_version: string;
  max_primary_refund_cents: number;
}

export interface FamilyCancelPlateRequest {
  subscription_id: string;
  version: string;
  items: CancelRefundItemChoice[];
}

export interface FamilyCancelRequest {
  request_id: string;
  family_version: string;
  location_id?: string;
  group_id: string;
  subscription_id: string;
  mode: FamilyCancelMode;
  new_primary_member_id?: string | null;
  reason_code: string;
  reason_note?: string;
  cancel_timing?: CancelTiming;
  plates: FamilyCancelPlateRequest[];
}

export interface FamilyCancelOutcome {
  pending: boolean;
  noop?: boolean;
  group_id?: string;
  left_member_ids?: string[];
  new_primary_member_id?: string | null;
  auto_picked?: boolean;
  dissolved?: boolean;
  closed?: boolean;
}

export interface FamilyCancelPlateResult {
  subscription_id: string;
  cancelled: boolean;
  status: CancellationStatus | 'skipped';
  replayed: boolean;
  refund: CancelRefundResult['refund'] | null;
  cancellation_id: string | null;
  error: { code: string; message: string } | null;
}

export interface FamilyCancelResult {
  mode: FamilyCancelMode;
  retry: boolean;
  /** Every plate cancelled and the family updated. */
  complete: boolean;
  plates: FamilyCancelPlateResult[];
  family: FamilyCancelOutcome;
}

/** The body svc-order sends with a 4xx; `code` is the stable key the panel words. */
export interface CancelRefundErrorBody {
  status: false;
  status_code: number;
  code?: string;
  message?: string;
}

export interface CancelRefundTarget {
  orderId?: string | null;
  subscriptionId?: string | null;
  locationId?: string | null;
}

@Injectable({ providedIn: 'root' })
export class CancelRefundService {
  private readonly api = inject(ApiService);

  getOptions(target: CancelRefundTarget): Observable<ApiResponse<CancelRefundOptions>> {
    const params: Record<string, string> = {};
    if (target.orderId) params['order_id'] = target.orderId;
    if (target.subscriptionId) params['subscription_id'] = target.subscriptionId;
    if (target.locationId) params['location_id'] = target.locationId;
    return this.api.get<CancelRefundOptions>('order/cancellations/options', params);
  }

  execute(body: CancelRefundRequest): Observable<ApiResponse<CancelRefundResult>> {
    return this.api.post<CancelRefundResult>('order/cancellations', body);
  }

  /** CWS-1947: claim this purchase while the operator decides. */
  takeHold(target: CancelRefundTarget): Observable<ApiResponse<CancelRefundHold>> {
    const body: Record<string, string> = {};
    if (target.orderId) body['order_id'] = target.orderId;
    if (target.subscriptionId) body['subscription_id'] = target.subscriptionId;
    if (target.locationId) body['location_id'] = target.locationId;
    return this.api.post<CancelRefundHold>('order/cancellations/hold', body);
  }

  /** CWS-1947: give it back on close so the order is not held for the whole window. */
  releaseHold(holdId: string): Observable<ApiResponse<{ released: boolean }>> {
    return this.api.delete<{ released: boolean }>(
      `order/cancellations/hold/${encodeURIComponent(holdId)}`
    );
  }

  /** CWS-1579: every plate of the membership's family, each with its own options. */
  getFamilyOptions(
    subscriptionId: string,
    locationId?: string | null
  ): Observable<ApiResponse<FamilyCancelOptions>> {
    const params: Record<string, string> = { subscription_id: subscriptionId };
    if (locationId) params['location_id'] = locationId;
    return this.api.get<FamilyCancelOptions>('order/cancellations/family/options', params);
  }

  /** CWS-1579: Cancel all, or Cancel primary only. */
  executeFamily(body: FamilyCancelRequest): Observable<ApiResponse<FamilyCancelResult>> {
    return this.api.post<FamilyCancelResult>('order/cancellations/family', body);
  }

  retryRefund(cancellationId: string): Observable<ApiResponse<CancelRefundResult>> {
    return this.api.post<CancelRefundResult>(
      `order/cancellations/${encodeURIComponent(cancellationId)}/retry-refund`,
      {}
    );
  }
}
